import dayjs from 'dayjs';
import Highcharts from 'highcharts';
import { HighchartsReact, HighchartsReactRefObject } from 'highcharts-react-official';
import 'highcharts/modules/accessibility';
import { useRef } from 'react';
import { Card } from '~/components';
import { formatCurrency, transformISOToDMYDate } from '~/utils';
import { usePerformanceChart } from './hooks';
import { strings } from './strings';
import { StyledSpinner, StyledSpinnerLabel, ChartCaption, ChartIndicator } from './styles';
import { PerformanceChartCardProps } from './types';

export const PerformanceChartCard = ({ reportingPeriodYears }: PerformanceChartCardProps) => {
    const chartComponentRef = useRef<HighchartsReactRefObject>(null);
    const { chartTitle, chartSubTitle, categories, values, isLoading } = usePerformanceChart(reportingPeriodYears);

    const options: Highcharts.Options = {
        chart: {
            type: 'line',
            plotBackgroundColor: '#f8f9fb',
        },
        credits: {
            enabled: false,
        },
        title: {
            text: chartTitle,
            style: { color: '#051a3f', fontSize: '1em' },
        },
        subtitle: {
            text: chartSubTitle,
            style: { color: '#051a3f', fontSize: '1em' },
        },
        accessibility: {
            description: 'Chart showing the historic performance of the portfolio over the selected reporting period',
        },
        xAxis: {
            categories: categories,
            tickInterval: 30 * (reportingPeriodYears || 1),
            tickLength: 10,
            tickWidth: 1,
            tickColor: '#d9dbdd',
            gridLineColor: '#d9dbdd',
            gridLineWidth: 1,
            lineColor: '#d9dbdd',
            labels: {
                formatter: function () {
                    return `${dayjs(this.value).format(`MMM 'YY`)}`;
                },
                style: { color: '#0a2f73' },
            },
        },
        yAxis: {
            title: {
                text: undefined,
            },
            labels: {
                formatter: function () {
                    return `£${parseFloat(this.value.toString()).toLocaleString('en-UK', {
                        maximumFractionDigits: 0,
                    })}`;
                },
                style: { color: '#0a2f73' },
            },
            gridLineColor: '#d9dbdd',
            lineColor: '#d9dbdd',
        },
        tooltip: {
            formatter: function () {
                return (
                    'The portfolio value on <b>' +
                    transformISOToDMYDate(this.category) +
                    '</b> was <b>£' +
                    formatCurrency(this.y as number) +
                    '</b>'
                );
            },
        },
        series: [
            {
                type: 'line',
                data: values,
                color: '#0a2f73',
                name: 'Client portfolio',
            },
        ],
        plotOptions: {
            series: {
                events: {
                    legendItemClick: function (e) {
                        e.preventDefault();
                    },
                },
            },
        },
        legend: { enabled: false },
    };
    return (
        <Card heading={strings.portfolioPerformance}>
            {isLoading ? (
                <div>
                    <StyledSpinner title="Performance chart" />
                    <StyledSpinnerLabel>Building performance chart</StyledSpinnerLabel>
                </div>
            ) : (
                <>
                    <HighchartsReact highcharts={Highcharts} options={options} ref={chartComponentRef} />
                    <ChartCaption>
                        <ChartIndicator />
                        Client pension portfolio
                    </ChartCaption>
                </>
            )}
        </Card>
    );
};
